/*───────────────────────────────────────────────────────────────
 *  File: src/utils/FileManager.js          — fully regenerated 2025-10-23
 *
 *  Browser-side helpers that call /api/files.php
 *
 *  • All keys live under:  <companySubdomain>/Leases/…
 *  • Exports:
 *      companySlug()   → string  (prefers /companyHome/:subdomain route)
 *      uploadFile()    → PUT via presigned URL • returns key
 *      listFiles()     → one-level (Delimiter="/") • returns [{type,Key,…}]
 *      signedUrl()     → 5-min presigned GET (string)
 *      folderOcrText() → fetches <folder>/payload.ocr.json text (lowercased)
 *  • Transparent auth:
 *      – Automatically forwards ?token=… from current URL to files.php
 *      – Always sends { company: companySlug() } on every action
 *──────────────────────────────────────────────────────────────*/

const API_ENDPOINT = "https://house.ogendeavors.com/api/files.php";

/*──────── helpers ───────────────────────────────────────────*/
function cleanHeaders(obj = {}) {
  const h = new Headers();
  Object.entries(obj).forEach(([k, v]) => {
    if (!k) return;
    const lk = k.toLowerCase();
    if (lk === "host" || lk === "content-length") return;
    h.set(k, v);
  });
  return h;
}

async function postJSON(payload) {
  // auto-forward ?token=… from the current URL AND ensure company=subdomain
  let merged = payload || {};
  try {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      const tkn = sp.get("token");
      if (tkn && !merged.token) merged = { ...merged, token: tkn };
      if (!merged.company) merged = { ...merged, company: companySlug() };
    }
  } catch {}
  const res = await fetch(API_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(merged),
    credentials: "include",
  });
  if (!res.ok) {
    let txt = "";
    try {
      txt = await res.text();
    } catch {}
    throw new Error(`API ${merged?.action || ""} failed: ${res.status} ${txt}`);
  }
  return res.json();
}

function niceNameFrom(originalName) {
  const raw = originalName || "file";
  const dot = raw.lastIndexOf(".");
  const base0 = dot === -1 ? raw : raw.slice(0, dot);
  const ext = dot === -1 ? "" : raw.slice(dot);
  const base1 = base0
    .replace(/[^\w\- ]+/g, "")
    .trim()
    .replace(/\s+/g, " ");
  const base = (base1 || "file").slice(0, 50);
  const stamp = new Date().toISOString().replace(/[:.]/g, "").slice(0, 15); // YYYYMMDDTHHMMSS
  const rand = Math.random().toString(36).slice(2, 8);
  return `${base}_${stamp}_${rand}${ext}`;
}

/**
 * Raw company identifier (subdomain). Resolution order:
 *  1) /companyHome/:subdomain route segment (tokenized link)
 *  2) ?company= / ?sub= / ?tenant= query param
 *  3) First hostname label (tenant subdomain)
 *  4) localStorage keys (companySubdomain or companyName)
 *  5) "Default"
 */
export function companySlug() {
  if (typeof window !== "undefined") {
    try {
      const { pathname, hostname, search } = window.location;

      // Prefer /companyHome/<subdomain>
      const m = pathname.match(/\/companyhome\/([^\/\?]+)/i);
      if (m && m[1]) return decodeURIComponent(m[1]);

      // Then query params
      const sp = new URLSearchParams(search);
      const qp = sp.get("company") || sp.get("sub") || sp.get("tenant");
      if (qp) return qp;

      // Then first label of hostname
      const first = hostname.replace(/^www\./, "").split(".")[0];
      if (first) return first;
    } catch {}
  }
  // Fallbacks
  if (typeof localStorage !== "undefined") {
    const ls =
      localStorage.getItem("companySubdomain") ||
      localStorage.getItem("companyName");
    if (ls) return ls;
  }
  return "Default";
}

/*──────────────── uploadFile  –  PUT with presign ────────────
 * @param file        File | Blob    (Blob without .name OK → “placeholder”)
 * @param folderPath  string         relative to Leases/  ("" for root, "Cook 111 #37A")
 * @param userId      string         uploader id for metadata
 * Returns: S3 key written.
 *────────────────────────────────────────────────────────────*/
export async function uploadFile(file, folderPath = "", userId = "web") {
  const rawName = file?.name || "placeholder";
  const niceName = niceNameFrom(rawName);

  const clean = (folderPath || "").replace(/^\/+|\/+$/g, ""); // trim slashes
  const key = [companySlug(), "Leases", clean, niceName]
    .filter(Boolean)
    .join("/");

  const contentType =
    (file && typeof file.type === "string" && file.type) ||
    "application/octet-stream";

  // Request a presigned PUT with the exact key
  const { request } = await postJSON({
    action: "putSigned",
    key,
    contentType,
    userId,
  });

  const headers = cleanHeaders(request?.headers);
  const body =
    typeof file?.arrayBuffer === "function"
      ? await file.arrayBuffer()
      : file || new Blob([]);

  const putRes = await fetch(request?.url, {
    method: request?.method || "PUT",
    headers,
    body,
  });
  if (!putRes.ok) {
    const t = await putRes.text().catch(() => "");
    throw new Error(`S3 upload failed (${putRes.status}): ${t}`);
  }
  return key;
}

/*──────────────── listFiles — one level (Delimiter="/") ──────
 * @param prefix  string  relative to company root (e.g. "Leases/" or "Leases/Foo/")
 * Returns mixed array (folders first, then files), each item:
 *   { type: "folder", Key }   // Key has NO trailing slash
 *   { type: "file",   Key, LastModified, Size }
 *────────────────────────────────────────────────────────────*/
export async function listFiles(prefix = "") {
  const clean = (prefix || "").replace(/^\/+/, ""); // keep trailing slash if caller passes it
  const items = await postJSON({
    action: "list",
    prefix: clean,
  });
  return Array.isArray(items) ? items : [];
}

/*──────────────── signedUrl – 5-min presigned GET ───────────*/
export async function signedUrl(key) {
  const { url } = await postJSON({ action: "signedUrl", key });
  return url;
}

/*──────────────── folderOcrText (optional) ───────────────────
 * Looks for  <folder>/payload.ocr.json  and returns its .text (lower-cased),
 * or "" on miss. Accepts prefix either with or without <company>/.
 *────────────────────────────────────────────────────────────*/
export async function folderOcrText(prefix) {
  if (!prefix) return "";
  const root = companySlug();
  const full = prefix.startsWith(`${root}/`) ? prefix : `${root}/${prefix}`;
  const key = `${full.replace(/\/?$/, "/")}payload.ocr.json`;

  try {
    const url = await signedUrl(key);
    const res = await fetch(url);
    if (!res.ok) return "";
    const json = await res.json();
    return (json?.text || "").toLowerCase();
  } catch {
    return "";
  }
}
